/* RESPONSIBLE TEAM: team-reporting */

import { cn, Table, Button } from '@intercom/surge';
import { SmallDownArrowIcon, SmallUpArrowIcon } from '@intercom/surge/icons';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import { buildTableData, type TableCell } from '@/features/reporting-charts/transforms/table';
import type {
  ChartConfig,
  ChartDataResponse,
  RenderContext,
} from '@/features/reporting-charts/types';

const PAGE_SIZE = 10;

interface TableChartProps {
  data: ChartDataResponse;
  config: ChartConfig;
  renderContext: RenderContext;
  className?: string;
}

function getCellKey(cell: TableCell, index: number): string {
  return `${index}-${cell.value}`;
}

function getRowKey(row: TableCell[], index: number): string {
  return `${index}-${row[0]?.value ?? ''}`;
}

export function TableChart({ data, config, renderContext, className }: TableChartProps) {
  const [lastData, setLastData] = useState(data);
  const [displayedRowCount, setDisplayedRowCount] = useState(PAGE_SIZE);

  // Reset pagination when new data arrives (render-phase update avoids the
  // extra render that useEffect would introduce). Compare the `data` prop
  // directly — it's a stable reference from the caller that only changes
  // when the API returns a new response.
  if (lastData !== data) {
    setLastData(data);
    setDisplayedRowCount(PAGE_SIZE);
  }

  const tableData = buildTableData(data, config, renderContext);
  const allRows = tableData.rows;

  const hasMoreRows = displayedRowCount < allRows.length;
  const displayedRows = allRows.slice(0, displayedRowCount);

  const loadMoreRows = () => {
    setDisplayedRowCount((prev) => Math.min(prev + PAGE_SIZE, allRows.length));
  };

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="flex-1 min-h-0 overflow-auto">
        <Table rootClassName="overflow-visible">
          <Table.Header>
            <Table.Row>
              {tableData.columns.map((column) => (
                <Table.HeaderCell
                  key={column.key}
                  className="sticky top-0 z-10 whitespace-nowrap min-w-[140px] bg-base-module font-semibold"
                >
                  {column.label}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tableData.summaryRow && (
              <Table.Row className="border-b-4 font-semibold">
                {tableData.summaryRow.map((cell, cellIndex) => (
                  <Table.Cell key={getCellKey(cell, cellIndex)}>
                    {cellIndex === 0 ? <Trans>Summary</Trans> : <CellContent cell={cell} />}
                  </Table.Cell>
                ))}
              </Table.Row>
            )}
            {displayedRows.map((row, rowIndex) => (
              <Table.Row key={getRowKey(row, rowIndex)}>
                {row.map((cell, cellIndex) => (
                  <Table.Cell
                    key={getCellKey(cell, cellIndex)}
                    className={cn('min-w-[120px]', cell.targetStatus)}
                  >
                    <CellContent cell={cell} />
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
      {hasMoreRows && (
        <div className="flex justify-center items-center pt-4 shrink-0">
          <Button variant="secondary" onClick={loadMoreRows}>
            <Trans>Load more</Trans>
          </Button>
        </div>
      )}
    </div>
  );
}

function CellContent({ cell }: { cell: TableCell }) {
  if (!cell.fractionLabel && !cell.comparison) {
    return <>{cell.value}</>;
  }

  return (
    <span className="inline-flex items-center gap-1">
      <span>
        {cell.value}
        {cell.fractionLabel && <span className="text-muted"> {cell.fractionLabel}</span>}
      </span>
      {cell.comparison && <ComparisonIndicator comparison={cell.comparison} />}
    </span>
  );
}

function ComparisonIndicator({ comparison }: { comparison: NonNullable<TableCell['comparison']> }) {
  const ArrowIcon = comparison.changeDirection === 'up' ? SmallUpArrowIcon : SmallDownArrowIcon;

  const isGoodChange =
    (comparison.improvementDirection === 'increasing' && comparison.changeDirection === 'up') ||
    (comparison.improvementDirection === 'decreasing' && comparison.changeDirection === 'down');

  return (
    <span
      className={cn('inline-flex items-center gap-1', isGoodChange ? 'text-success' : 'text-error')}
    >
      <ArrowIcon />
      {comparison.formattedDelta}
    </span>
  );
}
